import type { CostpointType, Tag, VATRate } from '@finapp/types';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { apiUrl, fetchWithAuth } from '../config/api';
import { vatService } from '../services/vatService';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: BulkEditData) => void;
  selectedCount: number;
  totalAmount?: number;
}

export interface BulkEditData {
  costpoint?: CostpointType;
  costpointAmount?: number;
  costpointMode?: 'full' | 'partial';
  tagIds?: number[];
  tagOperation?: 'add' | 'remove' | 'replace';
  // VAT fields
  hasVat?: boolean;
  vatRate?: number;
}

const costpointOptions: {
  value: CostpointType;
  label: string;
  description: string;
}[] = [
  { value: 'cs', label: 'CS', description: 'Counter-Strike related expenses' },
  { value: 'pubg', label: 'PUBG', description: 'PUBG related expenses' },
  { value: 'admin', label: 'Admin', description: 'Administrative expenses' },
  {
    value: 'other',
    label: 'Other',
    description: 'Other miscellaneous expenses',
  },
];

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedCount,
  totalAmount = 0,
}) => {
  // Costpoint state
  const [enableCostpoint, setEnableCostpoint] = useState(false);
  const [selectedCostpoint, setSelectedCostpoint] = useState<CostpointType>('admin');
  const [costpointAmount, setCostpointAmount] = useState<number>(0);
  const [costpointMode, setCostpointMode] = useState<'full' | 'partial'>('full');

  // Tag state
  const [enableTags, setEnableTags] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Set<number>>(new Set());
  const [tagOperation, setTagOperation] = useState<'add' | 'remove' | 'replace'>('add');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // VAT state
  const [enableVAT, setEnableVAT] = useState(false);
  const [hasVat, setHasVat] = useState(false);
  const [vatRate, setVatRate] = useState<number>(25.5); // Default to current Finnish standard VAT
  const [availableVATRates, setAvailableVATRates] = useState<VATRate[]>([]);

  // Fetch VAT rates
  const fetchVATRates = useCallback(async () => {
    try {
      const rates = await vatService.getVatRates();
      setAvailableVATRates(rates);

      // Set default to current general VAT rate
      const generalVAT = rates.find((r: VATRate) => r.name === 'General VAT');
      if (generalVAT) {
        setVatRate(generalVAT.rate);
      }
    } catch (error) {
      console.error('Failed to fetch VAT rates:', error);
      // Keep default rate if fetch fails
    }
  }, []);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(apiUrl('tags'));
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAvailableTags(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEnableCostpoint(false);
      setEnableTags(false);
      setEnableVAT(false);
      setSelectedCostpoint('admin');
      setCostpointAmount(0);
      setCostpointMode('full');
      setSelectedTags(new Set());
      setTagOperation('add');
      setSearchQuery('');
      setHasVat(false);
      setVatRate(25.5); // Will be updated by fetchVATRates
      fetchTags();
      fetchVATRates();
    }
  }, [isOpen, fetchVATRates, fetchTags]);

  const handleTagToggle = (tagId: number) => {
    const newSelected = new Set(selectedTags);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelectedTags(newSelected);
  };

  const handleSelectAllTags = () => {
    if (selectedTags.size === filteredTags.length) {
      setSelectedTags(new Set());
    } else {
      setSelectedTags(new Set(filteredTags.map((tag) => tag.id)));
    }
  };

  const handleSave = () => {
    const data: BulkEditData = {};

    if (enableCostpoint) {
      data.costpoint = selectedCostpoint;
      data.costpointAmount = costpointMode === 'full' ? 0 : costpointAmount;
      data.costpointMode = costpointMode;
    }

    if (enableTags && selectedTags.size > 0) {
      data.tagIds = Array.from(selectedTags);
      data.tagOperation = tagOperation;
    }

    if (enableVAT) {
      data.hasVat = hasVat;
      data.vatRate = hasVat ? vatRate : 0;
    }

    onSave(data);
    onClose();
  };

  // Filter tags based on search query
  const filteredTags = availableTags.filter(
    (tag) =>
      tag.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center">
      <div className="relative top-4 sm:top-10 p-4 sm:p-5 border w-full max-w-2xl sm:w-[700px] shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto m-4 sm:m-0">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Bulk Edit {selectedCount} Transaction
            {selectedCount !== 1 ? 's' : ''}
          </h3>

          {totalAmount > 0 && (
            <div className="mb-6 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-blue-800">
                Total amount:{' '}
                <span className="font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'EUR',
                  }).format(Math.abs(totalAmount))}
                </span>
              </p>
            </div>
          )}

          <div className="space-y-6">
            {/* Costpoint Section */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="enable-costpoint"
                  checked={enableCostpoint}
                  onChange={(e) => setEnableCostpoint(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="enable-costpoint"
                  className="ml-2 text-sm font-medium text-gray-900"
                >
                  Assign Costpoints
                </label>
              </div>

              {enableCostpoint && (
                <div className="space-y-4 ml-6">
                  {/* Costpoint Selection */}
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-2">Select Costpoint</p>
                    <div className="space-y-2">
                      {costpointOptions.map((option) => (
                        <label key={option.value} className="flex items-start">
                          <input
                            type="radio"
                            name="costpoint"
                            value={option.value}
                            checked={selectedCostpoint === option.value}
                            onChange={(e) => setSelectedCostpoint(e.target.value as CostpointType)}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{option.label}</div>
                            <div className="text-xs text-gray-500">{option.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Assignment Mode */}
                  {totalAmount > 0 && (
                    <div>
                      <p className="block text-sm font-medium text-gray-700 mb-2">
                        Assignment Mode
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="mode"
                            value="full"
                            checked={costpointMode === 'full'}
                            onChange={(e) => setCostpointMode(e.target.value as 'full' | 'partial')}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-900">
                            Assign full transaction amounts
                          </span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="mode"
                            value="partial"
                            checked={costpointMode === 'partial'}
                            onChange={(e) => setCostpointMode(e.target.value as 'full' | 'partial')}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-900">
                            Assign specific amount to each
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Partial Amount Input */}
                  {costpointMode === 'partial' && (
                    <div>
                      <label
                        htmlFor="bulk-costpoint-amount"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Amount per transaction (€)
                      </label>
                      <input
                        id="bulk-costpoint-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={costpointAmount}
                        onChange={(e) => setCostpointAmount(parseFloat(e.target.value) || 0)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* VAT Section */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="enable-vat"
                  checked={enableVAT}
                  onChange={(e) => setEnableVAT(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enable-vat" className="ml-2 text-sm font-medium text-gray-900">
                  Set VAT Settings
                </label>
              </div>

              {enableVAT && (
                <div className="space-y-4 ml-6">
                  {/* VAT Checkbox */}
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={hasVat}
                        onChange={(e) => {
                          setHasVat(e.target.checked);
                          if (!e.target.checked) {
                            setVatRate(0);
                          } else if (vatRate === 0) {
                            // Set to current general VAT rate or default
                            const generalVAT = availableVATRates.find(
                              (r) => r.name === 'General VAT'
                            );
                            setVatRate(generalVAT ? generalVAT.rate : 25.5);
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        These transactions include VAT
                      </span>
                    </label>
                  </div>

                  {/* VAT Rate Selection */}
                  {hasVat && (
                    <div>
                      <label
                        htmlFor="bulk-vat-rate"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        VAT Rate
                      </label>
                      <select
                        id="bulk-vat-rate"
                        value={vatRate}
                        onChange={(e) => setVatRate(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        {availableVATRates.map((rate) => (
                          <option key={rate.id} value={rate.rate}>
                            {rate.rate}% ({rate.name})
                          </option>
                        ))}
                        {availableVATRates.length === 0 && (
                          <>
                            <option value={25.5}>25.5% (General VAT)</option>
                            <option value={14.0}>14.0% (Reduced - Food)</option>
                            <option value={10.0}>10.0% (Reduced - Books)</option>
                            <option value={0.0}>0.0% (Zero VAT)</option>
                          </>
                        )}
                      </select>
                    </div>
                  )}

                  {/* VAT Information */}
                  <div className="bg-blue-50 p-3 rounded-md">
                    <div className="text-sm text-blue-800">
                      <div className="font-medium mb-1">VAT Impact:</div>
                      {hasVat ? (
                        <div>
                          • Transactions will be marked as including {vatRate}% VAT
                          <br />• Net amounts (excluding VAT) will be used for budget comparisons
                          <br />• VAT amounts will be calculated and tracked separately
                        </div>
                      ) : (
                        <div>
                          • Transactions will be marked as VAT-free
                          <br />• Full transaction amounts will be used for budget comparisons
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tags Section */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="enable-tags"
                  checked={enableTags}
                  onChange={(e) => setEnableTags(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enable-tags" className="ml-2 text-sm font-medium text-gray-900">
                  Manage Tags
                </label>
              </div>

              {enableTags && (
                <div className="space-y-4 ml-6">
                  {/* Tag Operation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Operation
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="operation"
                          value="add"
                          checked={tagOperation === 'add'}
                          onChange={(e) =>
                            setTagOperation(e.target.value as 'add' | 'remove' | 'replace')
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-900">Add tags</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="operation"
                          value="replace"
                          checked={tagOperation === 'replace'}
                          onChange={(e) =>
                            setTagOperation(e.target.value as 'add' | 'remove' | 'replace')
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-900">Replace tags</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="operation"
                          value="remove"
                          checked={tagOperation === 'remove'}
                          onChange={(e) =>
                            setTagOperation(e.target.value as 'add' | 'remove' | 'replace')
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-900">Remove tags</span>
                      </label>
                    </div>
                  </div>

                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Search Tags
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by tag name..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Tag Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Select Tags ({selectedTags.size} selected)
                      </label>
                      <button
                        onClick={handleSelectAllTags}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {selectedTags.size === filteredTags.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    {loading ? (
                      <div className="text-center py-4 text-gray-500">Loading tags...</div>
                    ) : (
                      <div className="border border-gray-300 rounded-md max-h-40 overflow-y-auto">
                        {filteredTags.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            {searchQuery ? 'No tags match your search' : 'No tags available'}
                          </div>
                        ) : (
                          <div className="p-2 space-y-1">
                            {filteredTags.map((tag) => (
                              <label
                                key={tag.id}
                                className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                  selectedTags.has(tag.id) ? 'bg-blue-50' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedTags.has(tag.id)}
                                  onChange={() => handleTagToggle(tag.id)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div className="ml-3 flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {tag.display_name}
                                  </div>
                                  {tag.usage_count > 0 && (
                                    <div className="text-xs text-gray-500">
                                      Used {tag.usage_count} time
                                      {tag.usage_count !== 1 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!enableCostpoint && !enableTags && !enableVAT}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
